import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, XCircle, CalendarX, Loader2, Clock, Phone, MessageCircle, Mail, CalendarPlus } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";

const PHONE = "+31628856762";
const PHONE_DISPLAY = "+31 6 28856762";
const WA_NUMBER = "31628856762";

export default function CancelPage() {
  const { token } = useParams();
  const [appt, setAppt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState(null); // 'cancelled' | 'already' | 'error'
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    api.get(`/public/appointment/${token}`)
      .then((r) => setAppt(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  const cancel = async () => {
    setWorking(true);
    try {
      const { data } = await api.post(`/public/appointment/${token}/cancel`);
      setResult(data.status === "already_cancelled" ? "already" : "cancelled");
    } catch (err) {
      setErrorMsg(formatApiError(err.response?.data?.detail));
      setResult("error");
    } finally { setWorking(false); }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-5 py-16 font-sans">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2.5 font-display text-xl text-ink">
            <img src="/logo-round.png" alt="" className="w-8 h-8" /> Nelleke Holt
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E8E2D9] p-8 shadow-[0_8px_30px_rgb(74,63,63,0.06)]">
          {loading && (
            <div className="py-10 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-brand" /></div>
          )}

          {!loading && notFound && (
            <Center icon={<XCircle className="w-12 h-12 text-ink-muted" strokeWidth={1.3} />}
              title="Afspraak niet gevonden"
              text="Deze annuleerlink is niet (meer) geldig. Neem gerust contact op met Nelleke." />
          )}

          {!loading && appt && !result && appt.status === "cancelled" && (
            <Center icon={<CalendarX className="w-12 h-12 text-brand" strokeWidth={1.3} />}
              title="Al geannuleerd"
              text="Deze afspraak is al geannuleerd." />
          )}

          {!loading && appt && !result && appt.status !== "cancelled" && (
            <div>
              <h1 className="text-2xl font-display text-center mb-2">Afspraak annuleren</h1>
              <p className="text-sm text-ink-muted text-center mb-6">Beste {appt.customer_name}, wil je deze afspraak annuleren?</p>
              <div className="rounded-xl bg-cream-subtle/60 p-5 mb-6 space-y-1">
                <div className="font-medium">{appt.treatment_name}</div>
                <div className="text-sm text-ink-muted flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" /> {appt.when.charAt(0).toUpperCase() + appt.when.slice(1)}
                </div>
              </div>
              {appt.cancellable ? (
                <div className="space-y-3">
                  <Button onClick={cancel} disabled={working} data-testid="confirm-cancel-btn"
                    className="w-full rounded-full bg-brand hover:bg-brand-hover text-white h-12">
                    {working ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ja, annuleer mijn afspraak"}
                  </Button>
                  <p className="text-xs text-ink-muted text-center">
                    Annuleren kan tot {appt.cancel_hours_before} uur voor de afspraak.
                  </p>
                </div>
              ) : (
                <div className="text-center py-4">
                  <div className="flex justify-center mb-4">
                    <CalendarX className="w-10 h-10 text-brand" strokeWidth={1.3} />
                  </div>
                  <h2 className="text-xl font-display mb-2">Online annuleren niet meer mogelijk</h2>
                  <p className="text-sm text-ink-muted leading-relaxed mb-6">
                    Deze afspraak kan tot {appt.cancel_hours_before} uur van tevoren online geannuleerd worden.
                    Neem gerust even contact op met Nelleke, dan lossen we het samen op.
                  </p>
                  <ContactActions />
                </div>
              )}
            </div>
          )}

          {result === "cancelled" && (
            <div className="text-center py-4">
              <div className="flex justify-center mb-4">
                <CheckCircle2 className="w-12 h-12 text-green-600" strokeWidth={1.3} />
              </div>
              <h2 className="text-xl font-display mb-2">Afspraak geannuleerd</h2>
              <p className="text-sm text-ink-muted leading-relaxed mb-6">
                Je afspraak is geannuleerd, bedankt voor het doorgeven. Wil je een nieuw
                moment inplannen? Dien gerust direct een nieuwe reservering in.
              </p>
              <Button asChild data-testid="new-reservation-btn"
                className="w-full rounded-full bg-brand hover:bg-brand-hover text-white h-12">
                <a href="/#reserveren"><CalendarPlus className="w-4 h-4 mr-2" /> Nieuwe afspraak aanvragen</a>
              </Button>
            </div>
          )}
          {result === "already" && (
            <Center icon={<CalendarX className="w-12 h-12 text-brand" strokeWidth={1.3} />}
              title="Al geannuleerd" text="Deze afspraak was al geannuleerd." />
          )}
          {result === "error" && (
            <Center icon={<XCircle className="w-12 h-12 text-brand" strokeWidth={1.3} />}
              title="Annuleren niet gelukt" text={errorMsg} />
          )}
        </div>

        <div className="text-center mt-6">
          <a href="/" className="text-sm text-ink-muted hover:text-brand transition-colors">Terug naar de website</a>
        </div>
      </div>
    </div>
  );
}

function Center({ icon, title, text }) {
  return (
    <div className="text-center py-4">
      <div className="flex justify-center mb-4">{icon}</div>
      <h2 className="text-xl font-display mb-2">{title}</h2>
      <p className="text-sm text-ink-muted leading-relaxed">{text}</p>
    </div>
  );
}

function ContactActions() {
  return (
    <div className="space-y-3">
      <Button asChild data-testid="cancel-call-btn"
        className="w-full rounded-full bg-brand hover:bg-brand-hover text-white h-12">
        <a href={`tel:${PHONE}`}><Phone className="w-4 h-4 mr-2" /> Bel {PHONE_DISPLAY}</a>
      </Button>
      <Button asChild variant="outline" data-testid="cancel-whatsapp-btn"
        className="w-full rounded-full h-12 border-[#E8E2D9]">
        <a href={`https://wa.me/${WA_NUMBER}`} target="_blank" rel="noreferrer">
          <MessageCircle className="w-4 h-4 mr-2" /> Stuur een WhatsApp
        </a>
      </Button>
      <Button asChild variant="ghost" data-testid="cancel-contact-btn"
        className="w-full rounded-full h-11 text-ink-muted hover:text-brand">
        <a href="/#contact"><Mail className="w-4 h-4 mr-2" /> Naar het contactformulier</a>
      </Button>
    </div>
  );
}
